import { env } from "@/core/config/env";
import type {
  Campaign,
  CampaignAudiencePreview,
  CampaignContactConsent,
  CampaignDashboard,
  CampaignPreset,
  CampaignRecipient,
  CampaignSegment,
  CampaignSettings,
  CampaignSuppression,
  CampaignTemplate,
  CreateCampaignRequest,
  SaveCampaignSegmentRequest,
  SaveCampaignTemplateRequest,
} from "@/core/types/campaigns";
import { httpClient } from "./httpClient";

const demoSettings: CampaignSettings = {
  requireApproval: true,
  requireCommercialConsent: true,
  smsLimit: 2,
  emailLimit: 3,
  socialLimit: 2,
  rollingDays: 7,
  quietHoursEnabled: true,
  quietStartMinute: 1260,
  quietEndMinute: 480,
  timeZoneId: "Iran Standard Time",
};

export const campaignsApi = {
  async dashboard(): Promise<CampaignDashboard> {
    if (env.isDemo)
      return {
        totalCampaigns: 0,
        activeCampaigns: 0,
        pendingApproval: 0,
        sent: 0,
        failed: 0,
        opened: 0,
        clicked: 0,
        converted: 0,
      };
    return (
      await httpClient.get<CampaignDashboard>("/api/v1/campaigns/dashboard")
    ).data;
  },
  async list(): Promise<Campaign[]> {
    if (env.isDemo) return [];
    return (await httpClient.get<Campaign[]>("/api/v1/campaigns")).data;
  },
  async create(request: CreateCampaignRequest): Promise<Campaign> {
    return (await httpClient.post<Campaign>("/api/v1/campaigns", request)).data;
  },
  async update(id: string, request: CreateCampaignRequest): Promise<Campaign> {
    return (
      await httpClient.request<Campaign>(`/api/v1/campaigns/${id}`, {
        method: "PUT",
        body: request,
      })
    ).data;
  },
  async preview(id: string): Promise<CampaignAudiencePreview> {
    return (
      await httpClient.get<CampaignAudiencePreview>(
        `/api/v1/campaigns/${id}/preview`,
      )
    ).data;
  },
  async action(
    id: string,
    action: "publish" | "approve" | "pause" | "resume" | "cancel",
  ): Promise<Campaign> {
    return (
      await httpClient.post<Campaign>(`/api/v1/campaigns/${id}/${action}`, {})
    ).data;
  },
  async recipients(id: string): Promise<CampaignRecipient[]> {
    return (
      await httpClient.get<CampaignRecipient[]>(
        `/api/v1/campaigns/${id}/recipients`,
      )
    ).data;
  },
  async segments(): Promise<CampaignSegment[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<CampaignSegment[]>("/api/v1/campaigns/segments")
    ).data;
  },
  async saveSegment(
    id: string | null,
    request: SaveCampaignSegmentRequest,
  ): Promise<CampaignSegment> {
    return (
      await httpClient.request<CampaignSegment>(
        id ? `/api/v1/campaigns/segments/${id}` : "/api/v1/campaigns/segments",
        { method: id ? "PUT" : "POST", body: request },
      )
    ).data;
  },
  async deleteSegment(id: string): Promise<void> {
    await httpClient.request<void>(`/api/v1/campaigns/segments/${id}`, {
      method: "DELETE",
    });
  },
  async templates(): Promise<CampaignTemplate[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<CampaignTemplate[]>("/api/v1/campaigns/templates")
    ).data;
  },
  async saveTemplate(
    id: string | null,
    request: SaveCampaignTemplateRequest,
  ): Promise<CampaignTemplate> {
    return (
      await httpClient.request<CampaignTemplate>(
        id
          ? `/api/v1/campaigns/templates/${id}`
          : "/api/v1/campaigns/templates",
        { method: id ? "PUT" : "POST", body: request },
      )
    ).data;
  },
  async deleteTemplate(id: string): Promise<void> {
    await httpClient.request<void>(`/api/v1/campaigns/templates/${id}`, {
      method: "DELETE",
    });
  },
  async presets(): Promise<CampaignPreset[]> {
    if (env.isDemo) return [];
    return (await httpClient.get<CampaignPreset[]>("/api/v1/campaigns/presets"))
      .data;
  },
  async createFromPreset(code: string): Promise<Campaign> {
    return (
      await httpClient.post<Campaign>(
        `/api/v1/campaigns/presets/${encodeURIComponent(code)}/create`,
        {},
      )
    ).data;
  },
  async activatePresetJourney(code: string): Promise<Campaign> {
    return (
      await httpClient.post<Campaign>(
        `/api/v1/campaigns/presets/${encodeURIComponent(code)}/activate`,
        {},
      )
    ).data;
  },
  async settings(): Promise<CampaignSettings> {
    if (env.isDemo) return demoSettings;
    return (
      await httpClient.get<CampaignSettings>("/api/v1/campaigns/settings")
    ).data;
  },
  async updateSettings(request: CampaignSettings): Promise<CampaignSettings> {
    if (env.isDemo) return request;
    return (
      await httpClient.request<CampaignSettings>("/api/v1/campaigns/settings", {
        method: "PUT",
        body: request,
      })
    ).data;
  },
  async consents(search?: string): Promise<CampaignContactConsent[]> {
    if (env.isDemo) return [];
    const query = search?.trim()
      ? `?search=${encodeURIComponent(search.trim())}`
      : "";
    return (
      await httpClient.get<CampaignContactConsent[]>(
        `/api/v1/campaigns/consents${query}`,
      )
    ).data;
  },
  async updateConsent(request: {
    contactId: string;
    channel: string;
    purpose: "commercial";
    status: "unknown" | "optIn" | "optOut";
    source: string;
  }): Promise<void> {
    await httpClient.post<void>("/api/v1/campaigns/consents", request);
  },
  async suppressions(): Promise<CampaignSuppression[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<CampaignSuppression[]>(
        "/api/v1/campaigns/suppressions",
      )
    ).data;
  },
  async addSuppression(request: {
    channel: string;
    destination: string;
    reason: string;
    untilUtc?: string | null;
  }): Promise<CampaignSuppression> {
    return (
      await httpClient.post<CampaignSuppression>(
        "/api/v1/campaigns/suppressions",
        request,
      )
    ).data;
  },
  async deleteSuppression(id: string): Promise<void> {
    await httpClient.request<void>(`/api/v1/campaigns/suppressions/${id}`, {
      method: "DELETE",
    });
  },
};
